import { ObservableQuery, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'

import { gql } from '@web/gql'
import { PageOwnerQuery, PageOwnerQueryVariables, PageOwner_UserFragment, User_ProfileFragment } from '@web/gql/graphql'
import { isTypename } from '@web/libs/apollo/typeGuard'

import useMe from './useMe'

export type PageOwner = PageOwner_UserFragment | User_ProfileFragment | null

export const pageOwnerQuery = gql(`
  query PageOwner($id: ObjectID!) {
    pageOwner: user(id: $id) {
      __typename
      ... on User_Succeed {
        user {
          ...PageOwner_User
        }
      }
    }
  }
`)

interface UsePageOwnerOutput {
  loading: boolean
  pageOwner?: PageOwner
  isMyPage: boolean
  refetchPageOwner: ObservableQuery<PageOwnerQuery, PageOwnerQueryVariables>['refetch']
}

const usePageInfo = (): UsePageOwnerOutput => {
  const { me, loading: meLoading } = useMe()

  const router = useRouter()
  const pageOwnerId = router.query.userId?.toString() || router.query.objectId?.toString()

  const isMyPage = !pageOwnerId || Boolean(me && pageOwnerId && me.id === pageOwnerId)

  const {
    data,
    loading,
    refetch: refetchPageOwner,
  } = useQuery(pageOwnerQuery, {
    variables: { id: pageOwnerId! },
    skip: isMyPage,
  })

  if (isMyPage) {
    return { isMyPage, loading: false, pageOwner: me, refetchPageOwner }
  }

  if (loading || meLoading) {
    return { isMyPage, loading: true, refetchPageOwner }
  }

  if (data && isTypename(data.pageOwner, 'User_Succeed')) {
    return { isMyPage, loading, pageOwner: data.pageOwner.user, refetchPageOwner }
  }

  return { isMyPage, loading, pageOwner: null, refetchPageOwner }
}

usePageInfo.fragment = gql(`
  fragment PageOwner_User on User {
    id
    idno
    profile {
      nickname
      imageUrl
      originImageUrl
      introduction
      updated
      border {
        name
        imageUrl
      }
    }
    stat {
      followingCount
      followerCount
      followRequestCount
      followRequesterCount
      reviewCount
      imageReviewCount
      imageCount
      blockCount
    }
    isFollowRequester
    isFriend
    didIRequestFollow
    isBlocked
    isAutoAcceptingFollow
    isThemeContentProvider
  }
`)

export default usePageInfo
